import React, { useMemo, useState, useEffect } from 'react';
import PivotTableUI from 'react-pivottable/PivotTableUI';
import 'react-pivottable/pivottable.css';
import Plot from 'react-plotly.js';
import createPlotlyRenderers from 'react-pivottable/PlotlyRenderers';
import TableRenderers from 'react-pivottable/TableRenderers';
import { Checkbox, FormControl, ListItemText, MenuItem, Select } from '@mui/material';
import MDBox from '../../controls/MDBox';
import MDTypography from '../../controls/MDTypography';
import MDButton from '../../controls/MDButton';
import { FileExcel } from 'react-bootstrap-icons';
import { Image, Refresh } from '@mui/icons-material';
import ExcelJS from 'exceljs';
import html2canvas from 'html2canvas';

/**
 * PivotTableChart - Componente de tabla pivotante para análisis multidimensional
 * 
 * @component
 * @param {Object} props - Propiedades del componente
 * @param {Array<Object>} props.data - Array de objetos con los datos a analizar
 * @param {string} props.title - Título del análisis
 * @param {string} [props.observaciones] - Notas adicionales opcionales
 * @param {string} [props.storageKey='pivotTableConfig'] - Clave para localStorage
 * 
 * @example
 * const data = [
 *   { region: 'Norte', producto: 'A', ventas: 1000, fecha: '2024-01' },
 *   { region: 'Sur', producto: 'B', ventas: 1500, fecha: '2024-01' },
 * ];
 * 
 * <PivotTableChart 
 *   data={data}
 *   title="Análisis de Ventas"
 *   observaciones="Datos del primer trimestre"
 *   storageKey="sales-pivot"
 * />
 */
const PivotTableChart = ({
  data,
  title,
  observaciones,
  storageKey = 'pivotTableConfig',
  valueFields
}) => {
  // Solo usar TableRenderers (sin gráficos)
  // const PlotlyRenderers = createPlotlyRenderers(Plot);

  const metricValueField = '__pivotMetricValue';
  const metricLabelField = 'Columnas dinámicas';

  const defaultAggregatorName = 'Suma entera';

  const availableValueFields = useMemo(() => {
    if (Array.isArray(valueFields) && valueFields.length > 0) {
      return valueFields;
    }
    if (!data || data.length === 0) {
      return [];
    }
    const keys = Object.keys(data[0]);
    return keys.filter(key => typeof data[0][key] === 'number');
  }, [data, valueFields]);

  const getDefaultConfig = (sourceData) => {
    if (!sourceData || sourceData.length === 0) {
      return {
        rows: [],
        cols: [],
        vals: [],
        aggregatorName: defaultAggregatorName,
        rendererName: 'Table'
      };
    }

    const keys = Object.keys(sourceData[0]);
    const textColumns = [];
    const numericColumns = [];

    keys.forEach(key => {
      const sampleValue = sourceData[0][key];
      if (typeof sampleValue === 'number') {
        numericColumns.push(key);
      } else if (typeof sampleValue === 'string' || sampleValue === null || sampleValue === undefined) {
        textColumns.push(key);
      }
    });

    return {
      rows: textColumns.slice(0, 1),
      cols: textColumns.slice(1, 2),
      vals: (availableValueFields.length > 0 ? availableValueFields : numericColumns).slice(0, 1),
      selectedValueFields: (availableValueFields.length > 0 ? availableValueFields : numericColumns).slice(0, 1),
      aggregatorName: defaultAggregatorName,
      rendererName: 'Table'
    };
  };

  const defaultConfig = useMemo(() => getDefaultConfig(data), [data, availableValueFields]);
  const [pivotState, setPivotState] = useState(defaultConfig);

  const sanitizePersistedState = (state) => {
    if (!state || typeof state !== 'object') {
      return defaultConfig;
    }

    const sanitizeArray = (value) =>
      Array.isArray(value)
        ? value.filter((item) => typeof item === 'string' && item !== metricLabelField && item !== metricValueField)
        : [];

    const sanitizedValueFilter = Object.entries(state.valueFilter || {}).reduce((accumulator, [key, value]) => {
      if (key === metricLabelField || key === metricValueField || !value || typeof value !== 'object') {
        return accumulator;
      }

      accumulator[key] = value;
      return accumulator;
    }, {});

    const sanitizedSelectedValueFields = sanitizeArray(state.selectedValueFields || state.vals);

    return {
      rows: sanitizeArray(state.rows),
      cols: sanitizeArray(state.cols),
      vals: sanitizeArray(state.vals),
      selectedValueFields:
        sanitizedSelectedValueFields.length > 0
          ? sanitizedSelectedValueFields
          : defaultConfig.selectedValueFields,
      aggregatorName:
        state.aggregatorName && Object.prototype.hasOwnProperty.call(allowedAggregators, state.aggregatorName)
          ? state.aggregatorName
          : defaultAggregatorName,
      rendererName: typeof state.rendererName === 'string' ? state.rendererName : 'Table',
      valueFilter: sanitizedValueFilter,
      rowOrder: typeof state.rowOrder === 'string' ? state.rowOrder : 'key_a_to_z',
      colOrder: typeof state.colOrder === 'string' ? state.colOrder : 'key_a_to_z',
    };
  };

  const persistPivotState = (state) => {
    const sanitizedState = sanitizePersistedState(state);

    try {
      localStorage.setItem(storageKey, JSON.stringify(sanitizedState));
    } catch (error) {
      if (error?.name === 'QuotaExceededError') {
        console.warn('No se pudo guardar la configuración completa del pivot por límite de almacenamiento.');
      } else {
        throw error;
      }
    }

    return sanitizedState;
  };

  const selectedValueFields = useMemo(() => {
    const validSelectedFields = (pivotState?.selectedValueFields || []).filter((field) => availableValueFields.includes(field));

    if (validSelectedFields.length > 0) {
      return validSelectedFields;
    }

    return availableValueFields.slice(0, 1);
  }, [availableValueFields, pivotState]);

  const handleValueFieldsChange = (event) => {
    try {
      const nextVals = [...new Set((event.target.value || []).filter((field) => availableValueFields.includes(field)))];
      setPivotState((previousState) => {
        const nextState = {
          ...previousState,
          selectedValueFields: nextVals,
        };

        persistPivotState(nextState);
        return nextState;
      });
    } catch (error) {
      console.error('Error saving selected value fields:', error);
    }
  };

  const allowedAggregators = useMemo(() => {
    const decimalFormatter = new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    const integerFormatter = new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    const formatValue = (formatter) => (value) => {
      if (value === null || value === undefined || Number.isNaN(value)) {
        return '';
      }
      return formatter.format(value);
    };

    const createAggregator = ({ createState, pushValue, getValue, formatter }) =>
      () => () => {
        const outlet = () => (
          <MDBox sx={{ mt: 1.5, minWidth: 260 }}>
            <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
              Columnas
            </MDTypography>
            <FormControl fullWidth size="small">
            <Select
              multiple
              value={selectedValueFields}
              onChange={handleValueFieldsChange}
              displayEmpty
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return 'Seleccione columnas';
                }

                if (selected.length === 1) {
                  return selected[0];
                }

                return `${selected.length} columnas seleccionadas`;
              }}
            >
              {availableValueFields.map((field) => (
                <MenuItem key={field} value={field}>
                  <Checkbox checked={selectedValueFields.includes(field)} />
                  <ListItemText primary={field} />
                </MenuItem>
              ))}
            </Select>
            </FormControl>
          </MDBox>
        );

        return {
          ...createState(),
          push(record) {
            pushValue.call(this, record);
          },
          value() {
            return getValue.call(this);
          },
          format: formatter,
          numInputs: 0,
          outlet,
        };
      };

    return {
      'Suma': createAggregator({
        createState: () => ({ total: 0 }),
        pushValue(record) {
          const value = Number.parseFloat(record?.[metricValueField]);
          if (Number.isFinite(value)) {
            this.total += value;
          }
        },
        getValue() {
          return this.total;
        },
        formatter: formatValue(decimalFormatter),
      }),
      'Suma entera': createAggregator({
        createState: () => ({ total: 0 }),
        pushValue(record) {
          const value = Number.parseFloat(record?.[metricValueField]);
          if (Number.isFinite(value)) {
            this.total += value;
          }
        },
        getValue() {
          return this.total;
        },
        formatter: formatValue(integerFormatter),
      }),
      'Promedio': createAggregator({
        createState: () => ({ total: 0, count: 0 }),
        pushValue(record) {
          const value = Number.parseFloat(record?.[metricValueField]);
          if (Number.isFinite(value)) {
            this.total += value;
            this.count += 1;
          }
        },
        getValue() {
          if (this.count === 0) {
            return null;
          }
          return this.total / this.count;
        },
        formatter: formatValue(decimalFormatter),
      }),
      'Contar': createAggregator({
        createState: () => ({ count: 0 }),
        pushValue(record) {
          const value = Number.parseFloat(record?.[metricValueField]);
          if (Number.isFinite(value)) {
            this.count += 1;
          }
        },
        getValue() {
          return this.count;
        },
        formatter: formatValue(integerFormatter),
      }),
    };
  }, [availableValueFields, selectedValueFields]);

  const hiddenFromAggregators = useMemo(() => {
    if (!data || data.length === 0 || availableValueFields.length === 0) {
      return [];
    }
    const keys = Object.keys(data[0]);
    return keys.filter(key => !availableValueFields.includes(key));
  }, [data, availableValueFields]);

  const pivotInputData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    if (selectedValueFields.length === 0) {
      return data;
    }

    return data.flatMap((row) => selectedValueFields.reduce((result, field) => {
      const parsedValue = Number.parseFloat(row?.[field]);
      if (!Number.isFinite(parsedValue)) {
        return result;
      }

      result.push({
        ...row,
        [metricLabelField]: `${pivotState.aggregatorName || defaultAggregatorName} de ${field}`,
        [metricValueField]: parsedValue,
      });
      return result;
    }, []));
  }, [data, selectedValueFields, pivotState]);

  const normalizedPivotState = useMemo(() => {
    const nextCols = (pivotState.cols || []).filter((column) => column !== metricLabelField);

    if (selectedValueFields.length > 1) {
      nextCols.push(metricLabelField);
    }

    return {
      ...pivotState,
      cols: nextCols,
      vals: selectedValueFields.length > 0 ? [metricValueField] : [],
    };
  }, [pivotState, selectedValueFields]);

  const activeFiltersSummary = useMemo(() => {
    const valueFilter = pivotState?.valueFilter || {};
    const filteredAttributes = Object.keys(valueFilter).filter(
      (attribute) => valueFilter[attribute] && Object.keys(valueFilter[attribute]).length > 0
    );

    if (!data || data.length === 0 || filteredAttributes.length === 0) {
      return [];
    }

    return filteredAttributes.map((attribute) => {
      const excludedValues = new Set(Object.keys(valueFilter[attribute]));
      const allValues = [...new Set(data.map((row) => {
        const cellValue = row?.[attribute];
        if (cellValue === null || cellValue === undefined || cellValue === '') {
          return 'Sin valor';
        }
        return String(cellValue);
      }))].sort((left, right) => left.localeCompare(right, 'es'));

      const selectedValues = allValues.filter((value) => !excludedValues.has(value));

      return {
        attribute,
        totalCount: allValues.length,
        selectedCount: selectedValues.length,
        selectedValues,
      };
    });
  }, [data, pivotState]);

  // Load configuration from localStorage on mount or when data changes
  useEffect(() => {
    try {
      const savedConfig = localStorage.getItem(storageKey);
      if (savedConfig) {
        const parsedConfig = sanitizePersistedState(JSON.parse(savedConfig));
        
        // Validate that saved configuration is compatible with current data
        if (data && data.length > 0) {
          const currentKeys = Object.keys(data[0]);
          const configKeys = [...(parsedConfig.rows || []), ...(parsedConfig.cols || []), ...(parsedConfig.vals || [])];
          const isValid = configKeys.every(key => currentKeys.includes(key));
          const isAggregatorValid = Object.prototype.hasOwnProperty.call(
            allowedAggregators,
            parsedConfig.aggregatorName
          );
          const areValsAllowed = availableValueFields.length === 0 ||
            (parsedConfig.selectedValueFields || parsedConfig.vals || []).every((val) => availableValueFields.includes(val));
          
          if (isValid && isAggregatorValid && areValsAllowed) {
            setPivotState({
              ...defaultConfig,
              ...parsedConfig,
              selectedValueFields:
                parsedConfig.selectedValueFields?.length > 0
                  ? parsedConfig.selectedValueFields
                  : defaultConfig.selectedValueFields,
            });
          } else {
            // Reset to default if saved config is incompatible
            localStorage.removeItem(storageKey);
            setPivotState(defaultConfig);
          }
        } else {
          setPivotState({
            ...defaultConfig,
            ...parsedConfig,
            selectedValueFields:
              parsedConfig.selectedValueFields?.length > 0
                ? parsedConfig.selectedValueFields
                : defaultConfig.selectedValueFields,
          });
        }
      } else {
        setPivotState(defaultConfig);
      }
    } catch (error) {
      console.error('Error loading pivot configuration from localStorage:', error);
      setPivotState(defaultConfig);
    }
  }, [storageKey, defaultConfig, availableValueFields, allowedAggregators]);

  // Handle pivot state changes and save to localStorage
  const handlePivotChange = (state) => {
    try {
      // Validate that state is a valid object
      if (!state || typeof state !== 'object') {
        console.error('Invalid pivot state:', state);
        return;
      }
      setPivotState((previousState) => {
        const nextState = {
          ...previousState,
          ...state,
          aggregatorName:
            state.aggregatorName && allowedAggregators[state.aggregatorName]
              ? state.aggregatorName
              : defaultAggregatorName,
          selectedValueFields:
            previousState.selectedValueFields?.length > 0
              ? previousState.selectedValueFields
              : defaultConfig.selectedValueFields || [],
        };

        persistPivotState(nextState);
        return nextState;
      });
    } catch (error) {
      console.error('Error saving pivot configuration to localStorage:', error);
    }
  };

  // Reset configuration to default
  const handleReset = () => {
    try {
      localStorage.removeItem(storageKey);
      setPivotState(defaultConfig);
    } catch (error) {
      console.error('Error resetting pivot configuration:', error);
    }
  };

  // Export current pivot table to Excel
  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Pivot Analysis');

      // Get the pivot table element
      const pivotElement = document.querySelector('#pivot-container .pvtTable');
      if (!pivotElement) {
        alert('Por favor cambie a vista de tabla para exportar a Excel');
        return;
      }

      const rows = pivotElement.querySelectorAll('tr');
      const tableData = [];
      
      rows.forEach((row) => {
        const rowData = [];
        const cells = row.querySelectorAll('th, td');
        cells.forEach((cell) => {
          rowData.push(cell.textContent.trim());
        });
        if (rowData.length > 0) {
          tableData.push(rowData);
        }
      });

      // Add data to worksheet
      tableData.forEach((rowData, index) => {
        const excelRow = worksheet.addRow(rowData);
        
        // Style header rows (first few rows typically)
        // Using ARGB format (no # prefix required by ExcelJS)
        if (index < 2) {
          excelRow.eachCell((cell) => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: '1976d2' }
            };
            cell.font = { color: { argb: 'FFFFFF' }, bold: true };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
          });
        }
      });

      // Auto-size columns
      worksheet.columns.forEach((column) => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const columnLength = cell.value ? cell.value.toString().length : 10;
          if (columnLength > maxLength) {
            maxLength = columnLength;
          }
        });
        column.width = maxLength < 10 ? 10 : maxLength + 2;
      });

      // Download file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title || 'pivot-analysis'}.xlsx`;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Error al exportar a Excel. Por favor intente nuevamente.');
    }
  };

  // Capture and download as PNG
  const captureAndDownloadImage = async () => {
    const element = document.querySelector('#pivot-container');
    if (!element) {
      alert('No se pudo capturar la imagen. Por favor intente nuevamente.');
      return;
    }

    let url = null;
    try {
      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: 2,
      });
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `${title || 'pivot-analysis'}.png`;
      link.click();
    } catch (error) {
      console.error('Error capturing image:', error);
      alert('Error al capturar la imagen. Por favor intente nuevamente.');
    } finally {
      if (url) {
        URL.revokeObjectURL(url);
      }
    }
  };

  // Handle empty or invalid data
  if (!data || data.length === 0) {
    return (
      <MDBox p={3}>
        <MDTypography variant="h5" fontWeight="medium" mb={2}>
          {title}
        </MDTypography>
        <MDTypography variant="body2" color="text">
          No hay datos disponibles para el análisis pivotante
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox p={3}>
      {/* Header with title and action buttons */}
      <MDBox 
        style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '8px'
        }}
      >
        <MDTypography variant="h5" fontWeight="medium">
          {title}
        </MDTypography>
        <MDBox style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            startIcon={<Refresh />}
            onClick={handleReset}
          >
            Resetear
          </MDButton>
          <MDButton
            variant="outlined"
            color="success"
            size="small"
            startIcon={<FileExcel />}
            onClick={exportToExcel}
          >
            Excel
          </MDButton>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            startIcon={<Image />}
            onClick={captureAndDownloadImage}
          >
            PNG
          </MDButton>
        </MDBox>
      </MDBox>

      {(selectedValueFields.length > 0 || activeFiltersSummary.length > 0) && (
        <MDBox
          mb={2}
          px={2}
          py={1.5}
          sx={(theme) => ({
            border: `1px solid ${theme.palette.success.light}`,
            borderRadius: theme.borders.borderRadius.md,
            backgroundColor: theme.palette.success.light,
          })}
        >
          <MDTypography variant="button" fontWeight="bold" color="info" display="block" mb={1}>
            Filtros activos
          </MDTypography>
          {activeFiltersSummary.map(({ attribute, selectedCount, totalCount, selectedValues }) => {
            const previewValues = selectedValues.slice(0, 5).join(', ');
            const remainingCount = selectedValues.length - 5;
            const summaryText = selectedCount === 0
              ? 'sin valores visibles'
              : selectedCount === totalCount
                ? 'todos los valores visibles'
                : `${selectedCount} de ${totalCount}: ${previewValues}${remainingCount > 0 ? ` +${remainingCount} más` : ''}`;

            return (
              <MDTypography key={attribute} variant="caption" color="white" display="block" mb={0.5}>
                <strong>{attribute}:</strong> {summaryText}
              </MDTypography>
            );
          })}
             <MDTypography variant="button" fontWeight="bold" color="info" display="block" mb={1}>
            Columnas activas:
          </MDTypography>
          {selectedValueFields.length > 0 && (
            <MDTypography variant="caption" color="white" display="block" mb={activeFiltersSummary.length > 0 ? 1 : 0}>
               {selectedValueFields.join(', ')}
            </MDTypography>
          )}
          
        </MDBox>
      )}

      {/* Pivot Table Container */}
      <MDBox 
        id="pivot-container"
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          borderRadius: theme.borders.borderRadius.lg,
          padding: '16px',
          boxShadow: theme.boxShadows.sm,
          overflow: 'auto',
          color: theme.palette.text.primary,
          '& .pvtUi, & .pvtUi *': {
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.size?.sm || '0.875rem',
            color: theme.palette.text.primary,
          },
          '& .pvtUi': {
            textAlign: 'left',
          },
          '& .pvtUi table': {
            marginLeft: 0,
          },
          '& .pvtUi td': {
            textAlign: 'left',
            verticalAlign: 'top',
          },
          '& .pvtAxisContainer, & .pvtVals': {
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.grey[50],
            minHeight: '60px',
            padding: '8px',
            borderRadius: theme.borders.borderRadius.md,
          },
          '& .pvtAxisContainer.pvtRows': {
            backgroundColor: theme.palette.success.light,
            borderColor: theme.palette.success.main,
          },
          '& .pvtAxisContainer.pvtCols': {
            backgroundColor: theme.palette.info.light,
            borderColor: theme.palette.info.main,
          },
          '& .pvtVals': {
            backgroundColor: theme.palette.warning.light,
            borderColor: theme.palette.warning.main,
          },
          '& .pvtAxisContainer.pvtCols .pvtAttr': {
            backgroundColor: theme.palette.info.main,
            color: theme.palette.common.white,
            borderRadius: theme.borders.borderRadius.md,
            padding: '2px 6px',
            margin: '2px 4px 2px 0',
            border: `1px solid ${theme.palette.info.dark}`,
          },
          '& .pvtAxisContainer.pvtCols .pvtAttr:nth-of-type(even)': {
            backgroundColor: theme.palette.info.dark,
            borderColor: theme.palette.info.darker || theme.palette.info.dark,
          },
          '& .pvtAxisContainer.pvtRows .pvtAttr': {
            backgroundColor: theme.palette.success.main,
            color: theme.palette.common.white,
            borderRadius: theme.borders.borderRadius.md,
            padding: '2px 6px',
            margin: '2px 4px 2px 0',
            border: `1px solid ${theme.palette.success.dark}`,
          },
          '& .pvtAxisContainer.pvtRows .pvtAttr:nth-of-type(even)': {
            backgroundColor: theme.palette.success.dark,
          },
          '& .pvtDropdown, & .pvtAttrDropdown, & select, & input': {
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.borders.borderRadius.md,
            padding: '4px 8px',
            backgroundColor: theme.palette.background.paper,
          },
          '& .pvtRendererArea': {
            marginTop: '16px',
          },
          '& .pvtTable': {
            fontSize: theme.typography.size?.sm || '0.875rem',
            borderCollapse: 'separate',
            borderSpacing: 0,
          },
          '& .pvtTable thead tr th': {
            backgroundColor: theme.palette.info.main,
            color: theme.palette.common.white,
            padding: '8px',
            fontWeight: theme.typography.fontWeightBold,
            borderBottom: `1px solid ${theme.palette.info.dark}`,
          },
          '& .pvtTable thead tr th.pvtColLabel': {
            backgroundColor: theme.palette.info.main,
          },
          '& .pvtTable thead tr th.pvtColLabel:nth-of-type(even)': {
            backgroundColor: theme.palette.info.dark,
          },
          '& .pvtTable thead tr th.pvtColLabel:nth-of-type(3n)': {
            backgroundColor: theme.palette.info.light,
            color: theme.palette.info.dark,
          },
          '& .pvtTable tbody tr th': {
            backgroundColor: theme.palette.success.main,
            color: theme.palette.common.white,
            padding: '8px',
            fontWeight: theme.typography.fontWeightBold,
            borderRight: `1px solid ${theme.palette.success.dark}`,
          },
          '& .pvtTable tbody tr td': {
            padding: '8px',
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          },
          '& .pvtTable tbody tr:nth-of-type(even) td': {
            backgroundColor: theme.palette.grey[50],
          },
          '& .pvtTotal, & .pvtTotalLabel, & .pvtGrandTotal': {
            fontWeight: theme.typography.fontWeightBold,
            backgroundColor: theme.palette.info.light,
          }
        })}
      >
        <PivotTableUI
          data={pivotInputData}
          onChange={handlePivotChange}
          aggregators={allowedAggregators}
          hiddenAttributes={[metricValueField, metricLabelField]}
          hiddenFromAggregators={[...hiddenFromAggregators, metricValueField, metricLabelField]}
          renderers={TableRenderers}
          rendererName="Table"
          {...normalizedPivotState}
        />
      </MDBox>

      {/* Optional observations */}
      {observaciones && (
        <MDTypography 
          variant="caption" 
          color="text" 
          mt={2} 
          style={{ 
            fontStyle: 'italic', 
            opacity: 0.7,
            display: 'block'
          }}
        >
          *{observaciones}
        </MDTypography>
      )}
    </MDBox>
  );
};

export default PivotTableChart;
